import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import styles from "./Navbar.module.css";
import AssetsPack from "../util/AssetsPack";
import { JokerButton, ButtonType, ButtonSize } from "material-ui-jokerpro";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ImageComponent from "./ImageComponent";
import { MOBILE_MAX_WIDTH_PX } from "../util/Constant";
import { motion } from "framer-motion";

gsap.registerPlugin(ScrollTrigger);

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuItemRef = useRef<(HTMLLIElement | null)[]>([]);
  const [width, setWidth] = useState<number>(window.innerWidth);

  const timeline = useRef<gsap.core.Timeline | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleSignUp = () => {
    navigate("/sign_up");
    if (isOpen) toggleMenu();
  };

  const handleLogIn = () => {
    navigate("/login");
    if (isOpen) toggleMenu();
  };

  const handleMain = () => {
    navigate("/main");
    if (isOpen) toggleMenu();
  };

  const toggleMenu = () => {
    if (!timeline.current) {
      setIsOpen(!isOpen);
      return;
    }

    if (isOpen) {
      timeline.current.reverse();
    } else {
      timeline.current.restart();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (width > MOBILE_MAX_WIDTH_PX && menuItemRef.current.length > 0) {
      const menuItems = menuItemRef.current.filter(Boolean) as HTMLElement[];

      gsap.set(menuItems, {
        clearProps: "all",
      });
    }
  }, [width]);

  const navRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 0) {
        // At the top — reset navbar
        nav.classList.remove(styles["scrolled"]);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down
        if (!nav.classList.contains(styles["scrolled"])) {
          nav.classList.add(styles["scrolled"]);
        }
      } else if (currentScrollY < lastScrollY && currentScrollY <= 10) {
        // Scrolling up & close to top
        nav.classList.remove(styles["scrolled"]);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [width]);

  useEffect(() => {
    const originalOverflow = "";

    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalOverflow;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const navbarData = [
    { to: "/joker_pro", title: t("nav_bar.joker_pro") },
    { to: "/joker_scratch", title: t("nav_bar.joker_scratch") },
    { to: "/learn", title: t("nav_bar.learn") },
    { to: "/download", title: t("nav_bar.download") },
    { to: "/rewards_program", title: t("nav_bar.rewards_program") },
    { to: "/support", title: t("nav_bar.support") },
  ];

  return (
    <motion.nav
      initial={{ opacity: 0, y: -40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 96,
        damping: 30,
      }}
      style={{
        position: "fixed",
        top: 0,
        width: "100%",
        zIndex: 1000,
      }}
      ref={navRef}
      className={styles["navbar"]}
    >
      {/* <nav ref={navRef} className={styles["navbar"]}> */}
      <div className={styles["navbar-container"]}>
        <div className={styles["navbar-logo"]} onClick={handleMain}>
          <ImageComponent src={AssetsPack.logos.JOKER_PRO_LOGO.default} />
        </div>
        <div
          ref={menuRef}
          className={clsx(styles["navbar-menu"], {
            [styles["open"]]: isOpen,
          })}
        >
          <ul className={styles["navbar-links"]}>
            {navbarData.map((data, index) => (
              <li
                ref={(el) => {
                  if (el) menuItemRef.current[index] = el;
                }}
                key={data.title}
              >
                <NavLink
                  to={data.to}
                  className={clsx(
                    location.pathname === data.to && styles["active"]
                  )}
                  onClick={isOpen ? toggleMenu : undefined}
                >
                  {data.title}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className={styles["navbar-mobile-buttons"]}>
            <JokerButton
              text={t("signup")}
              size={ButtonSize.Small}
              onClick={handleSignUp}
              type={ButtonType.Secondary}
              disabled
            />
            <JokerButton
              text={t("nav_bar.signin")}
              size={ButtonSize.Small}
              onClick={handleLogIn}
              type={ButtonType.Primary}
              icon={AssetsPack.icons.ICON_LOGIN.default}
              disabled
            />
          </div>
        </div>

        <div className={styles["navbar-buttons"]}>
          <JokerButton
            text={t("signup")}
            size={ButtonSize.Small}
            onClick={handleSignUp}
            type={ButtonType.Secondary}
            disabled
          />
          <JokerButton
            text={t("nav_bar.signin")}
            size={ButtonSize.Small}
            onClick={handleLogIn}
            type={ButtonType.Primary}
            icon={AssetsPack.icons.ICON_LOGIN.default}
            disabled
          />
        </div>

        <button className={styles["menu-button"]}>
          <ImageComponent
            src={
              isOpen
                ? AssetsPack.icons.ICON_CROSS.default
                : AssetsPack.icons.ICON_MENU.default
            }
            onClick={toggleMenu}
          />
        </button>
      </div>
      {/* </nav> */}
    </motion.nav>
  );
};

export default Navbar;
